import { css, styled } from '../../../common.styles';
import { baseColor, mobile, normalFont, primaryColor, primaryFont } from '../../../styles/general/theme';
import { BasicButtonStyle, ButtonProps } from '../general/common-button';
import { LazyFaIcon } from '../../icon/general/lazy-fa-icon';
import { marginInline } from '../../../styles/general/logical-properties';
import React from 'react';

export const SsgButtonStyle = css<{
  disabled?: boolean | undefined;
  darkBackground?: boolean | undefined;
  fullWidth?: boolean;
}>`
  ${BasicButtonStyle};
  width: ${(p) => (p.fullWidth ? '100%' : 'fit-content')};
  box-sizing: border-box;
  border-style: solid;
  border-width: 1px;
  letter-spacing: 1px;
  font-family: ${normalFont};
  padding: 12.5px 37px;
  background-color: transparent;
  border-radius: 4px;
  font-size: 16px;
  font-weight: 700;
  line-height: 120%;
  border-color: ${primaryColor(100)};
  color: ${primaryColor(100)};
  cursor: pointer;
  transition: all ease 0.2s;
  text-align: center;
`;

export const SsgButtonStyleNormal = css<{ disabled?: boolean | undefined }>`
  ${SsgButtonStyle};

  :hover {
    background-color: ${primaryColor(100)};
    color: ${baseColor(900)};
  }
`;

export const SsgSimpleButton = styled.a<{
  disabled?: boolean | undefined;
  transparentBackground?: boolean;
}>`
  ${SsgButtonStyleNormal};
`;

export const SsgButtonStyleFull = css<{ disabled?: boolean | undefined; reversedColors?: boolean }>`
  ${SsgButtonStyle};

  ${(p) =>
    !p.reversedColors
      ? css`
          background-color: ${primaryColor(100)};
          color: ${baseColor(900)};
        `
      : css`
          color: ${primaryColor(100)};
          background-color: ${baseColor(900)};
        `}

  :hover {
    ${(p) =>
      !p.reversedColors
        ? css`
            background-color: ${baseColor(900)};
            color: ${primaryColor(100)};
          `
        : css`
            color: ${baseColor(900)};
            background-color: ${primaryColor(100)};
          `}
  }
`;

export const SsgSimpleButtonFull = styled.a<{ disabled?: boolean | undefined; reversedColors?: boolean }>`
  ${SsgButtonStyleFull};
`;

export const SsgVDPagerButton = styled.button<{ disabled?: boolean | undefined }>`
  ${SsgButtonStyleFull};
  ${(p) =>
    p.disabled &&
    css`
      opacity: 0.5;
      pointer-events: none;

      :hover {
        background-color: ${primaryColor(100)};
        color: ${baseColor(900)};
        cursor: not-allowed;
      }
    `}
`;

export const SsgButtonStyles = {
  SsgButtonStyleNormal,
  SsgButtonStyleFull,
};

export const SsgBackButtonStyle = css`
  color: ${primaryColor(100)};
  font-size: 16px;
  font-weight: 700;
  letter-spacing: 0.16px;
  line-height: 24px;
  cursor: pointer;
  text-decoration: none;
  font-family: ${primaryFont};
`;

export const SsgChevron = styled(LazyFaIcon)`
  color: ${primaryColor(100)};
  ${marginInline(0, 8)}
  font-size: ${(p) => p.theme.fontSize.normal}px;
`;

const PrimaryButtonStyle = styled(SsgSimpleButton)<{
  disabled?: boolean | undefined;
  darkBackground?: boolean | undefined;
}>`
  color: ${(p) => (p.darkBackground ? primaryColor(100) : baseColor(900))};
  border-color: ${(p) => (p.darkBackground ? baseColor(900) : primaryColor(100))};
  background-color: ${(p) => (p.darkBackground ? baseColor(900) : primaryColor(100))};
  ${(p) =>
    p.transparentBackground &&
    css`
      background-color: ${p.transparentBackground ? 'transparent' : primaryColor(100)};
    `};

  @media (max-width: ${mobile}px) {
    width: 100%;
  }
  &:hover {
    border-color: ${(p) => (p.darkBackground ? baseColor(900) : primaryColor(100))};
    background: ${(p) => (p.darkBackground ? 'transparent' : baseColor(900))};
    color: ${(p) => (p.darkBackground ? baseColor(900) : primaryColor(100))};
    ${(p) =>
      p.disabled &&
      css`
        background-color: ${baseColor(300)};
        border-color: ${baseColor(300)};
        color: ${baseColor(900)};
      `};
  }

  ${(p) =>
    p.disabled &&
    css`
      background-color: ${baseColor(300)};
      border-color: ${baseColor(300)};
      color: ${baseColor(900)};
    `};
`;

const SecondaryButtonStyle = styled(SsgSimpleButton)<{ darkBackground?: boolean }>`
  color: ${(p) => (p.darkBackground ? baseColor(900) : primaryColor(100))};
  border-color: ${(p) => (p.darkBackground ? baseColor(900) : primaryColor(100))};
  background-color: ${(p) => (p.darkBackground ? 'transparent' : baseColor(900))};

  ${(p) =>
    p.transparentBackground &&
    css`
      background-color: ${p.transparentBackground ? 'transparent' : baseColor(900)};
    `};

  @media (max-width: ${mobile}px) {
    width: 100%;
  }
  &:hover {
    border-color: ${(p) => (p.darkBackground ? baseColor(900) : primaryColor(100))};
    background: ${(p) => (p.darkBackground ? baseColor(900) : primaryColor(100))};
    color: ${(p) => (p.darkBackground ? primaryColor(100) : baseColor(900))};

    ${(p) =>
      p.disabled &&
      css`
        color: ${baseColor(300)};
        border-color: ${baseColor(300)};
        background-color: transparent;
      `};
  }

  ${(p) =>
    p.disabled &&
    css`
      background-color: transparent;
      color: ${baseColor(300)};
      border-color: ${baseColor(300)};
      cursor: not-allowed;
    `};
`;

export const PrimaryButton = (props: ButtonProps) => {
  return (
    <PrimaryButtonStyle {...props} className={props?.className} onClick={props.onClick}>
      {props.children}
    </PrimaryButtonStyle>
  );
};

export const SecondaryButton = (props: ButtonProps) => {
  return (
    <SecondaryButtonStyle {...props} className={props?.className} onClick={props.onClick}>
      {props.children}
    </SecondaryButtonStyle>
  );
};

const SsgIconAndTextButtonWrapper = styled.a<{
  disabled?: boolean | undefined;
  darkBackground?: boolean;
  fullWidth?: boolean;
}>`
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: 18px;
  font-family: ${primaryFont};
  color: ${(p) => (p.darkBackground ? baseColor(900) : primaryColor(100))};
  cursor: pointer;
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: fit-content;
  padding: 12.5px 37px;
  box-sizing: border-box;

  @media (max-width: ${mobile}px) {
    width: 100%;
  }

  &:hover {
    text-decoration: none;
    font-weight: 700;
    ${(p) =>
      p.disabled &&
      css`
        font-weight: 400;
      `};
  }

  ${(p) =>
    p.disabled &&
    css`
      cursor: not-allowed;
      color: ${baseColor(300)};
    `};
`;

const SsgTextButtonWrapper = styled(SsgIconAndTextButtonWrapper)`
  justify-content: center;
`;

export const SsgTextButton: React.FC<ButtonProps> = (props) => {
  return <SsgTextButtonWrapper {...props}>{props.children}</SsgTextButtonWrapper>;
};

const Svg = styled.svg`
  width: 24px;
  height: 24px;
  path {
    fill: currentColor;
    transition: fill 0.2s ease;
  }
`;

export const SsgChevronRightArrowIcon: React.FC = () => (
  <Svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
    <path d="M9.816 7.456L8.832 8.456L12.816 12L8.832 15.544L9.856 16.544L15.184 12L9.816 7.456Z" />
  </Svg>
);

export const SsgChevronLeftArrowIcon: React.FC = () => (
  <Svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" style={{ transform: 'scaleX(-1)' }}>
    <path d="M9.816 7.456L8.832 8.456L12.816 12L8.832 15.544L9.856 16.544L15.184 12L9.816 7.456Z" />
  </Svg>
);

export const SsgArrowAndTextButton: React.FC<ButtonProps> = (props) => {
  const position = props.iconPosition || 'left';
  return (
    <SsgTextButtonWrapper {...props}>
      {position === 'left' && <SsgChevronLeftArrowIcon />}
      {props.children}
      {position === 'right' && <SsgChevronRightArrowIcon />}
    </SsgTextButtonWrapper>
  );
};

export const SsgButton = (props: ButtonProps): any => {
  switch (props?.variant?.toLowerCase()) {
    case 'primary':
      return <PrimaryButton {...props} />;
    case 'secondary':
      return <SecondaryButton {...props} />;
    case 'tertiary':
      return <SsgTextButtonWrapper {...props} />;
    case 'icon':
      return <SsgArrowAndTextButton {...props} />;
  }
};
